/**
 * Which Project Tracker project this quote prices — Amy, 30/10/2026 (Jay approved in GC): "link Costing to the Project
 * Tracker … similar to the Sample feature in Development", so the tracker's Costing column shows where the costing is
 * (In Progress → Waiting Manager / CEO Approval → Complete). Max chose, 31/10: pick the PRJ, and each SKU line of the
 * quote is matched to one of its SKUs by name — changeable by hand.
 *
 * Saved on its own (PUT …/tracker-link), not with the quote: a submitted quote is frozen, and the quotes waiting for an
 * approver are exactly the ones the tracker should be showing. So the band appears once the quote has a CP number, and
 * its Save is its own.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link2, Search, Loader2 } from 'lucide-react';
import { BACKEND_URL } from './backendConfig';
import './CpTrackerLink.css';

const API = `${BACKEND_URL}/api/pricing`;

/* name → the words in it, for matching "Tac. Sunset Chair" to the SKU "Tactical Sunset Chair" */
const words = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim().split(' ').filter((w) => w.length > 1 && w !== 'x' && w !== 'helinox');
const score = (a, b) => {
  const wa = words(a); const wb = new Set(words(b));
  return wa.filter((w) => wb.has(w) || [...wb].some((v) => v.startsWith(w) || w.startsWith(v))).length;
};
/* each quote line takes its best-matching SKU, best pairs first, one SKU per line */
export const matchLines = (lines, skus) => {
  const pairs = [];
  lines.forEach((l, i) => skus.forEach((s) => { const n = score(l, s.name); if (n > 0) pairs.push([n, i, s.id]); }));
  pairs.sort((a, b) => b[0] - a[0]);
  const out = lines.map(() => ''); const used = new Set();
  pairs.forEach(([, i, id]) => { if (!out[i] && !used.has(id)) { out[i] = id; used.add(id); } });
  return out;
};

export default function CpTrackerLink({ projectId, lines = [], canEdit = false }) {
  const { t } = useTranslation();
  const [link, setLink] = useState({ prj: '', skus: [] });
  const [project, setProject] = useState(null);       // { id, name, skus }
  const [editing, setEditing] = useState(false);
  const [q, setQ] = useState('');
  const [found, setFound] = useState([]);
  const [draft, setDraft] = useState({ project: null, skus: [] });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const fetchProject = useCallback(async (id) => {
    if (!id) return null;
    const r = await fetch(`${API}/tracker-projects?id=${encodeURIComponent(id)}`, { credentials: 'include' });
    const d = await r.json().catch(() => ({}));
    return (d.projects || [])[0] || null;
  }, []);

  useEffect(() => {
    let off = false;
    setEditing(false); setError(''); setProject(null); setLink({ prj: '', skus: [] });
    if (!projectId) return undefined;
    (async () => {
      try {
        const r = await fetch(`${API}/projects/${encodeURIComponent(projectId)}/tracker-link`, { credentials: 'include' });
        const d = await r.json().catch(() => ({}));
        if (off || !r.ok) return;
        setLink({ prj: d.prj || '', skus: d.skus || [] });
        if (d.prj) { const p = await fetchProject(d.prj); if (!off) setProject(p); }
      } catch (e) { /* the band says "not linked" */ }
    })();
    return () => { off = true; };
  }, [projectId, fetchProject]);

  useEffect(() => {
    if (!editing) return undefined;
    const h = setTimeout(async () => {
      try {
        const r = await fetch(`${API}/tracker-projects?q=${encodeURIComponent(q.trim())}`, { credentials: 'include' });
        const d = await r.json().catch(() => ({}));
        setFound(d.projects || []);
      } catch (e) { setFound([]); }
    }, 250);
    return () => clearTimeout(h);
  }, [q, editing]);

  if (!projectId) return null;

  const open = () => {
    setError(''); setQ(''); setEditing(true);
    setDraft({ project, skus: project ? lines.map((_, i) => link.skus[i] || '') : [] });
  };
  const pick = (p) => setDraft({ project: p, skus: matchLines(lines, p.skus) });
  const save = async (unlink) => {
    setBusy(true); setError('');
    try {
      const body = unlink ? { prj: '', skus: [] } : { prj: draft.project.id, skus: draft.skus };
      const r = await fetch(`${API}/projects/${encodeURIComponent(projectId)}/tracker-link`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || t('xCpLink.saveFailed'));
      setLink({ prj: d.prj || '', skus: d.skus || [] });
      setProject(unlink ? null : draft.project);
      setEditing(false);
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const linkedCount = link.skus.filter(Boolean).length;
  const skuName = (p, id) => (p?.skus || []).find((s) => s.id === id)?.name || '';

  return (
    <section className="cptl" aria-label={t('xCpLink.title')}>
      <div className="cptl-head">
        <Link2 size={15} aria-hidden="true" />
        <strong>{t('xCpLink.title')}</strong>
        {link.prj ? (
          <span className="cptl-sum">
            <b>{link.prj}</b>{project?.name ? ` · ${project.name}` : ''} — {t('xCpLink.linkedCount', { n: linkedCount, total: lines.length })}
          </span>
        ) : <span className="cptl-sum muted">{t('xCpLink.notLinked')}</span>}
        {canEdit && !editing && (
          <button type="button" className="sp-btn" onClick={open} data-cptl-edit>
            {link.prj ? t('xCpLink.change') : t('xCpLink.link')}
          </button>
        )}
      </div>
      {!editing && link.prj && (
        <ul className="cptl-lines">
          {lines.map((name, i) => (
            <li key={`${i}-${name}`}>
              <span>{name || `#${i + 1}`}</span>
              <span aria-hidden="true">→</span>
              {link.skus[i] ? <b>{link.skus[i]} {skuName(project, link.skus[i])}</b> : <em>{t('xCpLink.noSku')}</em>}
            </li>
          ))}
        </ul>
      )}
      {!editing && <p className="cptl-note">{t('xCpLink.note')}</p>}
      {editing && (
        <div className="cptl-edit">
          {!draft.project ? (
            <>
              <label className="cptl-search">
                <Search size={14} aria-hidden="true" />
                <input className="sp-input" autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('xCpLink.searchPh')} aria-label={t('xCpLink.searchPh')} data-cptl-search />
              </label>
              <ul className="cptl-found">
                {found.map((p) => (
                  <li key={p.id}>
                    <button type="button" onClick={() => pick(p)} data-cptl-pick={p.id}>
                      <b>{p.id}</b> {p.name} <span className="muted">· {t('xCpLink.nSkus', { n: p.skus.length })}</span>
                    </button>
                  </li>
                ))}
                {!found.length && <li className="muted">{t('xCpLink.noneFound')}</li>}
              </ul>
            </>
          ) : (
            <>
              <p className="cptl-picked">
                <b>{draft.project.id}</b> {draft.project.name}
                <button type="button" className="cptl-linkbtn" onClick={() => setDraft({ project: null, skus: [] })}>{t('xCpLink.other')}</button>
              </p>
              <table className="cptl-table">
                <thead><tr><th scope="col">{t('xCpLink.quoteLine')}</th><th scope="col">{t('xCpLink.trackerSku')}</th></tr></thead>
                <tbody>
                  {lines.map((name, i) => (
                    <tr key={`${i}-${name}`}>
                      <td>{name || `#${i + 1}`}</td>
                      <td>
                        <select className="sp-input" value={draft.skus[i] || ''} aria-label={`${t('xCpLink.trackerSku')} — ${name}`}
                          onChange={(e) => setDraft((d) => { const s = [...d.skus]; s[i] = e.target.value; return { ...d, skus: s }; })} data-cptl-sku={i}>
                          <option value="">{t('xCpLink.noSku')}</option>
                          {draft.project.skus.map((s) => <option key={s.id} value={s.id}>{s.id} · {s.name}</option>)}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
          {error && <p className="cptl-error" role="alert">{error}</p>}
          <div className="cptl-actions">
            {draft.project && (
              <button type="button" className="sp-btn primary" disabled={busy} onClick={() => save(false)} data-cptl-save>
                {busy && <Loader2 size={13} className="cpa-spin" />} {t('xCpLink.save')}
              </button>
            )}
            <button type="button" className="sp-btn" disabled={busy} onClick={() => setEditing(false)}>{t('xCpLink.cancel')}</button>
            {link.prj && <button type="button" className="sp-btn danger" disabled={busy} onClick={() => save(true)} data-cptl-unlink>{t('xCpLink.unlink')}</button>}
          </div>
        </div>
      )}
    </section>
  );
}
